import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";



export default function Contact() {
  const sectionRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll(
              ".animate-on-scroll, .animate-on-scroll-left, .animate-on-scroll-right"
            );
            elements.forEach((el) => el.classList.add("visible"));
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        throw new Error("Email service not configured.");
      }

      await emailjs.send(
        serviceId,
        templateId,
        {
          name: form.name,
          email: form.email,
          phone: form.phone || "Not provided",
          subject: form.subject || "General Inquiry",
          message: form.message,
        },
        publicKey
      );

      toast.success("Your enquiry has been sent successfully! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error("Form submission error:", err);
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#1A1A1A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 md:gap-20">
          {/* Contact Info */}
          <div className="animate-on-scroll-left lg:col-span-2">
            <div className="w-12 h-[2px] bg-gold mb-6" />
            <h2
              data-testid="contact-heading"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F9F9F7] tracking-[-0.02em]"
            >
              Get In Touch
            </h2>
            <p className="font-body text-base text-[#F9F9F7]/60 mt-6 leading-relaxed font-light">
              Ready to start your project? Reach out and let's discuss how we can
              bring your vision to life.
            </p>

            <div className="mt-12 space-y-8">
              <div className="flex items-start gap-4" data-testid="contact-email">
                <div className="w-10 h-10 flex items-center justify-center border border-[#F9F9F7]/20 text-gold flex-shrink-0">
                  <Mail size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <span className="font-body text-xs uppercase tracking-widest text-gold font-medium">
                    Email
                  </span>
                  <p className="font-body text-sm text-[#F9F9F7] mt-1">
                    info@giriinteriorsandconstructions.com
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phone">
                <div className="w-10 h-10 flex items-center justify-center border border-[#F9F9F7]/20 text-gold flex-shrink-0">
                  <Phone size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <span className="font-body text-xs uppercase tracking-widest text-gold font-medium">
                    Phone
                  </span>
                  <p className="font-body text-sm text-[#F9F9F7] mt-1">
                    +91-9739744479
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-location">
                <div className="w-10 h-10 flex items-center justify-center border border-[#F9F9F7]/20 text-gold flex-shrink-0">
                  <MapPin size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <span className="font-body text-xs uppercase tracking-widest text-gold font-medium">
                    Location
                  </span>
                  <p className="font-body text-sm text-[#F9F9F7] mt-1">
                    Bangalore, Karnataka, India
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="animate-on-scroll-right lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="relative z-10 bg-[#2A2A2A] border border-[#F9F9F7]/10 p-8 md:p-12 space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-gold font-medium mb-2 block">
                    Full Name *
                  </label>
                  <Input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    data-testid="input-name"
                    className="border-0 border-b border-[#F9F9F7]/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-gold placeholder:text-[#F9F9F7]/30 text-[#F9F9F7] font-body text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-gold font-medium mb-2 block">
                    Email Address *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    data-testid="input-email"
                    className="border-0 border-b border-[#F9F9F7]/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-gold placeholder:text-[#F9F9F7]/30 text-[#F9F9F7] font-body text-sm"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-gold font-medium mb-2 block">
                    Phone Number
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+91-XXXXXXXXXX"
                    data-testid="input-phone"
                    className="border-0 border-b border-[#F9F9F7]/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-gold placeholder:text-[#F9F9F7]/30 text-[#F9F9F7] font-body text-sm"
                  />
                </div>
                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-gold font-medium mb-2 block">
                    Subject
                  </label>
                  <Select
                    value={form.subject}
                    onValueChange={(val) => setForm({ ...form, subject: val })}
                  >
                    <SelectTrigger
                      data-testid="input-subject"
                      className="border-0 border-b border-[#F9F9F7]/20 rounded-none bg-transparent px-0 py-3 focus:ring-0 focus-visible:ring-0 font-body text-sm h-auto shadow-none text-[#F9F9F7] [&>svg]:text-[#F9F9F7]/50"
                    >
                      <SelectValue placeholder="Select a subject" className="placeholder:text-[#F9F9F7]/30" />
                    </SelectTrigger>
                    <SelectContent data-testid="subject-dropdown">
                      <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                      <SelectItem value="Project Discussion">Project Discussion</SelectItem>
                      <SelectItem value="Get a Quote">Get a Quote</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="font-body text-xs uppercase tracking-widest text-gold font-medium mb-2 block">
                  Message *
                </label>
                <Textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project..."
                  rows={5}
                  data-testid="input-message"
                  className="border-0 border-b border-[#F9F9F7]/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-gold placeholder:text-[#F9F9F7]/30 text-[#F9F9F7] font-body text-sm resize-none"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-enquiry-button"
                className="inline-flex items-center gap-2 font-body text-sm font-medium bg-gold text-charcoal px-10 py-4 hover:bg-gold-light transition-all duration-300 hover:-translate-y-[1px] hover:shadow-lg disabled:opacity-50 disabled:hover:translate-y-0 mt-4"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Enquiry
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
